<template>
  <div class="oauth">
    <div class="oauth__inner">
      <div class="oauth__brand">
        <h1 class="oauth__wordmark">pmm</h1>
      </div>

      <div class="al-card oauth__card">
        <template v-if="loading">
          <p class="oauth__loading">Loading…</p>
        </template>

        <template v-else-if="error">
          <span class="material-symbols-outlined oauth__icon oauth__icon--error">error</span>
          <h2 class="oauth__title">Authorization error</h2>
          <p class="oauth__subtitle">{{ error }}</p>
        </template>

        <template v-else>
          <span class="material-symbols-outlined oauth__icon">lock_open</span>
          <h2 class="oauth__title">Authorize access</h2>
          <p class="oauth__subtitle">
            <strong>{{ clientName }}</strong> is requesting access to your pmm account.
          </p>

          <ul class="oauth__scopes">
            <li v-for="scope in scopes" :key="scope" class="oauth__scope">
              <span class="material-symbols-outlined oauth__scope-icon">check_circle</span>
              {{ scopeLabel(scope) }}
            </li>
          </ul>

          <div class="oauth__actions">
            <button class="oauth__btn oauth__btn--deny" :disabled="submitting" @click="decide('deny')">
              Deny
            </button>
            <button class="oauth__btn oauth__btn--approve" :disabled="submitting" @click="decide('approve')">
              {{ submitting ? 'Authorizing…' : 'Authorize' }}
            </button>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useAuthStore } from '@/stores/authStore';
import { API_URL, authFetch } from '@/api.js';

const route = useRoute();
const router = useRouter();
const authStore = useAuthStore();

const loading = ref(true);
const submitting = ref(false);
const error = ref('');
const clientName = ref('');

const { client_id, redirect_uri, code_challenge, code_challenge_method, scope, state } = route.query;
const scopes = (scope || 'financial:read').split(' ');

const SCOPE_LABELS = {
  'financial:read': 'Read your accounts, expenses, incomes and loans',
  'financial:write': 'Create transactions and expenses',
  'statements:process': 'Import bank statement data',
};

function scopeLabel(s) {
  return SCOPE_LABELS[s] || s;
}

onMounted(async () => {
  if (!authStore.ready) await authStore.fetchUser();

  if (!authStore.isAuthenticated) {
    const next = encodeURIComponent(route.fullPath);
    router.replace(`/login?redirect=${next}`);
    return;
  }

  if (!client_id || !redirect_uri || !code_challenge) {
    error.value = 'Missing required OAuth parameters.';
    loading.value = false;
    return;
  }

  try {
    const res = await fetch(`${API_URL}/oauth/client-info?client_id=${encodeURIComponent(client_id)}`, {
      credentials: 'include',
    });
    if (!res.ok) throw new Error('Client not found.');
    const data = await res.json();
    clientName.value = data.name;
  } catch (e) {
    error.value = e.message;
  } finally {
    loading.value = false;
  }
});

async function decide(decision) {
  submitting.value = true;
  try {
    const res = await authFetch(`${API_URL}/oauth/authorize`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        client_id,
        redirect_uri,
        code_challenge,
        code_challenge_method: code_challenge_method || 'S256',
        scope: scope || 'financial:read',
        state: state || null,
        decision,
      }),
    });

    if (res.status === 401) { router.push('/login'); return; }
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.detail || 'Authorization failed.');
    }

    const { redirect_to } = await res.json();
    window.location.href = redirect_to;
  } catch (e) {
    error.value = e.message;
    submitting.value = false;
  }
}
</script>

<style scoped>
.oauth {
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  background: var(--al-background);
}

.oauth__inner {
  width: 100%;
  max-width: 420px;
  display: flex;
  flex-direction: column;
  gap: 2rem;
}

.oauth__brand { text-align: center; }

.oauth__wordmark {
  font-family: var(--al-font-headline);
  font-weight: 800;
  font-size: 2.75rem;
  letter-spacing: 0.03em;
  color: var(--al-primary);
  margin: 0;
}

.oauth__card {
  padding: 2rem;
  text-align: center;
}

.oauth__loading { color: var(--al-on-surface-variant); }

.oauth__icon {
  font-size: 2.5rem;
  color: var(--al-primary);
}

.oauth__icon--error { color: #c0392b; }

.oauth__title {
  font-family: var(--al-font-headline);
  font-size: 1.25rem;
  font-weight: 700;
  margin: 0.75rem 0 0.25rem;
  color: var(--al-on-surface);
}

.oauth__subtitle {
  font-size: 0.9rem;
  color: var(--al-on-surface-variant);
  margin: 0 0 1.5rem;
}

.oauth__scopes {
  list-style: none;
  padding: 0;
  margin: 0 0 1.75rem;
  text-align: left;
}

.oauth__scope {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0;
  font-size: 0.875rem;
  color: var(--al-on-surface);
  border-bottom: 1px solid var(--al-outline, #eee);
}

.oauth__scope:last-child { border-bottom: none; }

.oauth__scope-icon {
  font-size: 1.1rem;
  color: #27ae60;
}

.oauth__actions {
  display: flex;
  gap: 0.75rem;
}

.oauth__btn {
  flex: 1;
  padding: 0.625rem 1rem;
  border-radius: 6px;
  font-size: 0.9rem;
  font-weight: 600;
  cursor: pointer;
  border: none;
  transition: opacity 0.15s;
}

.oauth__btn:disabled { opacity: 0.5; cursor: not-allowed; }

.oauth__btn--approve {
  background: var(--al-primary, #1a73e8);
  color: #fff;
}

.oauth__btn--deny {
  background: transparent;
  border: 1.5px solid var(--al-outline, #ccc);
  color: var(--al-on-surface-variant);
}
</style>
